import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  Theme,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import SelectSku from "components/SelectSku";
import { useMemo } from "react";
import { withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import {
  CertificateSkuExtended,
  ICertificateSkuRequest,
} from "services/KnowfullyAdminClient";
import { CommonFormProps } from "types/common-types";
import useGlobalContext from "hooks/globalData";
import { useQuery } from "react-query";

const useStyles = (theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
});

type CertificateSkuFormProps = {
  certificateSkuId?: string | undefined;
  certificateId: string;
} & CommonFormProps<CertificateSkuExtended, ICertificateSkuRequest>;

const { Form } = withTypes<ICertificateSkuRequest>();

const CertificateSkuForm = ({
  certificateSkuId,
  certificateId,
  entity: certificateSku,
  onCancel,
  onSubmit,
}: CertificateSkuFormProps) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { certificateService } = useGlobalContext();

  const certificate = useQuery(["certificate", certificateId], () =>
    certificateService.getCertificate(certificateId)
  );

  const initialValues = useMemo<ICertificateSkuRequest>(
    () => ({
      id: certificateSkuId,
      skuId: certificateSku?.skuId,
    }),
    [certificateSkuId, certificateSku?.skuId]
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, submitting, submitError }) => (
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Typography>
              <T id="certificateSkuDialogTitle" />
            </Typography>
            <Grid container spacing={1}>
              <Field name="skuId">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={meta.error && meta.submitFailed}
                      required
                    >
                      <SelectSku
                        {...input}
                        organizationId={certificate.data?.data?.organizationId}
                        loading={certificate.isLoading}
                        label={t({
                          id: "certificateSkuSkuLabel",
                        })}
                        textFieldProps={{
                          error: meta.error && meta.submitFailed,
                        }}
                      />
                      {meta.error && meta.submitFailed && (
                        <FormHelperText>{meta.error}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            {submitting && <CircularProgress size={24} />}
            <Button
              disabled={submitting}
              onClick={onCancel}
            >
              {t({ id: "cancelButton" })}
            </Button>

            <Button
              type="submit"
              color="primary"
              disabled={submitting}
            >
              {t({ id: "certificateSkuCreateButtonLabel" })}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default CertificateSkuForm;
